// Cloudflare Turnstile token seam. In production a real invisible widget yields a fresh token at
// submit; with no sitekey (pre-launch) it yields "" so the funnel still runs end-to-end.
// PHI never touches Turnstile.

interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  execute: (widgetId: string, opts?: Record<string, unknown>) => Promise<void>;
  getResponse: (widgetId: string) => string;
  reset: (widgetId?: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";
let scriptPromise: Promise<void> | null = null;
let widgetId: string | null = null;
let containerEl: HTMLElement | null = null;

export function __resetTurnstileForTest(): void {
  scriptPromise = null;
  widgetId = null;
  containerEl = null;
}

function loadScript(): Promise<void> {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Failed to load Turnstile"));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

interface TokenOptions {
  /** Test hook: assume window.turnstile is already present, skip network script load. */
  skipScriptLoad?: boolean;
}

// Returns a fresh Turnstile token, or "" when Turnstile is not configured.
export async function getTurnstileToken(sitekey: string, opts: TokenOptions = {}): Promise<string> {
  if (!sitekey) return "";
  if (!opts.skipScriptLoad) {
    await loadScript();
  }
  const api = window.turnstile;
  if (!api) return "";

  if (widgetId === null) {
    containerEl = document.createElement("div");
    containerEl.style.display = "none";
    document.body.appendChild(containerEl);
    widgetId = api.render(containerEl, { sitekey, size: "invisible" });
  } else {
    api.reset(widgetId);
  }

  await api.execute(widgetId, { sitekey });
  return api.getResponse(widgetId);
}
